import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Download, RefreshCw, Star, XCircle, Archive } from "lucide-react";
import { providersApi } from "./api";
import type { ListFilters, ServiceProviderApplication } from "./types";
import { StatusBadge } from "./shared/StatusBadge";
import { FiltersBar } from "./shared/FiltersBar";
import { ApplicationsTable, type Column } from "./shared/ApplicationsTable";
import { ApplicationDrawer } from "./shared/ApplicationDrawer";
import { Section, Field, Link, Chips } from "./shared/InfoBlock";
import {
  countryOptions,
  formatCountry,
  formatDate,
  formatDateTime,
} from "./shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";
import { confirmAction } from "@/app/providers/ConfirmDialog";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "contacted", label: "Contactado" },
  { value: "verified", label: "Verificado" },
  { value: "featured", label: "Destacado" },
  { value: "rejected", label: "Rechazado" },
  { value: "archived", label: "Archivado" },
];

const PAGE_SIZE = 25;

export function ProvidersScreen() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, status, country]);

  const filters: ListFilters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      status: status || undefined,
      country: country || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, status, country, page],
  );

  const listQuery = useQuery({
    queryKey: ["applications", "providers", filters],
    queryFn: () => providersApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["applications", "providers", "detail", selectedId],
    queryFn: () => providersApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  const exportHref = `${import.meta.env.VITE_API_BASE || "https://api-nexus.mixcore.club"}${providersApi.exportCsv(filters)}`;

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Aplicaciones · Proveedores</p>
          <h1 className="nx-h1">Postulaciones de proveedores</h1>
          <p className="text-sm text-fg-muted mt-1">
            Proveedores de sonido, iluminación, catering, seguridad, etc. Verificá para que aparezcan en el catálogo público cuando se publique.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={exportHref} target="_blank" rel="noopener noreferrer" className="nx-btn-secondary">
            <Download size={14} /> Exportar CSV
          </a>
          <button
            onClick={() => listQuery.refetch()}
            className="nx-btn-secondary"
            disabled={listQuery.isFetching}
          >
            <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por negocio, ciudad o email…"
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
        country={country}
        onCountryChange={setCountry}
        countryOptions={countryOptions()}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando proveedores…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<ServiceProviderApplication>
          rows={listQuery.data?.applications ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      <ProviderDrawer
        selectedId={selectedId}
        data={detailQuery.data ?? null}
        loading={detailQuery.isLoading && selectedId !== null}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

const columns: Column<ServiceProviderApplication>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "business",
    header: "Negocio",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.businessName}</div>
        <div className="text-2xs text-fg-subtle">{r.businessType}</div>
      </div>
    ),
  },
  {
    key: "categories",
    header: "Categorías",
    cell: (r) => (
      <div className="flex flex-wrap gap-1">
        {r.categories.slice(0, 3).map((c) => (
          <span
            key={c}
            className="inline-flex items-center text-2xs bg-bg-subtle border border-border-subtle text-fg-muted rounded px-1.5 py-0.5"
          >
            {c}
          </span>
        ))}
        {r.categories.length > 3 && (
          <span className="text-2xs text-fg-subtle">+{r.categories.length - 3}</span>
        )}
      </div>
    ),
  },
  {
    key: "contact",
    header: "Contacto",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.contactName}</div>
        <div className="text-2xs text-fg-subtle break-all">{r.contactEmail}</div>
      </div>
    ),
  },
  {
    key: "location",
    header: "Ubicación",
    cell: (r) => (
      <div className="text-fg-muted text-xs">
        {r.city}, {formatCountry(r.country)}
      </div>
    ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => <StatusBadge status={r.status} />,
    width: "min",
  },
  {
    key: "createdAt",
    header: "Recibida",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

// Exportado: la Bandeja unificada (InboxScreen) reusa este mismo drawer.
export function ProviderDrawer({
  selectedId,
  data,
  loading,
  onClose,
}: {
  selectedId: number | null;
  data: ServiceProviderApplication | null;
  loading: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["applications", "providers"] });
  };

  const updateNotes = useMutation({
    mutationFn: (notes: string | null) =>
      providersApi.update(selectedId!, { internalNotes: notes }),
    onSuccess: () => {
      push({ kind: "success", message: "Notas guardadas." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudieron guardar las notas." }),
  });

  const updateStatus = useMutation({
    mutationFn: (newStatus: string) =>
      providersApi.update(selectedId!, { status: newStatus }),
    onSuccess: () => {
      push({ kind: "success", message: "Estado actualizado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo actualizar el estado." }),
  });

  const verify = useMutation({
    mutationFn: () => providersApi.verify(selectedId!),
    onSuccess: () => {
      push({ kind: "success", message: "Proveedor verificado. Listo para aparecer en el catálogo." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo verificar." }),
  });

  const reject = useMutation({
    mutationFn: (reason: string | undefined) => providersApi.reject(selectedId!, reason),
    onSuccess: () => {
      push({ kind: "success", message: "Proveedor rechazado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo rechazar." }),
  });

  return (
    <ApplicationDrawer<ServiceProviderApplication>
      open={selectedId !== null}
      data={data}
      loading={loading}
      title={data ? data.businessName : "Proveedor"}
      subtitle={data ? `#${data.id} · ${formatDateTime(data.createdAt)}` : undefined}
      onClose={onClose}
      onSaveNotes={(notes) => updateNotes.mutateAsync(notes).then(() => undefined)}
      renderBody={(d) => (
        <>
          <div className="flex items-center gap-2"><StatusBadge status={d.status} /></div>

          <Section title="Negocio">
            <Field label="Nombre" value={d.businessName} wide />
            <Field label="Tipo" value={d.businessType} />
            <Field label="Experiencia" value={d.experience} />
            <Field label="Categorías" value={<Chips items={d.categories} />} wide />
            <Field label="Descripción" value={d.description} wide />
            <Field label="Referencias" value={d.notableReferences ?? "—"} wide />
          </Section>

          <Section title="Cobertura">
            <Field label="País" value={formatCountry(d.country)} />
            <Field label="Región" value={d.region} />
            <Field label="Ciudad" value={d.city} />
            <Field label="Áreas que cubre" value={<Chips items={d.servesAreas} />} wide />
            <Field label="Sale de su región" value={d.travelsOutsideRegion ? "Sí" : "No"} />
          </Section>

          <Section title="Comercial & contacto">
            <Field label="Rango de precios" value={d.priceRange ?? "—"} wide />
            <Field label="ID tributario" value={d.hasTaxId ? d.taxIdValue ?? "Sí" : "No"} />
            <Field label="Nombre contacto" value={d.contactName} />
            <Field
              label="Email"
              value={
                <a className="text-accent hover:underline" href={`mailto:${d.contactEmail}`}>
                  {d.contactEmail}
                </a>
              }
              wide
            />
            <Field
              label="WhatsApp"
              value={
                <a
                  className="text-accent hover:underline"
                  href={`https://wa.me/${d.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {d.whatsapp}
                </a>
              }
              wide
            />
            <Field label="Sitio web" value={d.websiteUrl ? <Link href={d.websiteUrl}>{d.websiteUrl}</Link> : "—"} wide />
            <Field label="Instagram" value={d.instagramUrl ? <Link href={d.instagramUrl}>{d.instagramUrl}</Link> : "—"} wide />
            <Field label="Portfolio" value={d.portfolioUrl ? <Link href={d.portfolioUrl}>{d.portfolioUrl}</Link> : "—"} wide />
            <Field label="Comentarios" value={d.additionalComments ?? "—"} wide />
          </Section>

          {(d.verifiedAt || d.rejectedAt) && (
            <Section title="Historia">
              {d.verifiedAt && <Field label="Verificado" value={formatDateTime(d.verifiedAt)} wide />}
              {d.rejectedAt && (
                <>
                  <Field label="Rechazado" value={formatDateTime(d.rejectedAt)} wide />
                  {d.rejectionReason && <Field label="Razón" value={d.rejectionReason} wide />}
                </>
              )}
            </Section>
          )}
        </>
      )}
      renderActions={(d) => {
        const isVerified = d.status === "verified" || d.status === "featured";
        const isRejected = d.status === "rejected";
        return (
          <>
            {(d.status === "pending" || d.status === "contacted") && (
              <button
                onClick={() => updateStatus.mutate("contacted")}
                disabled={updateStatus.isPending || d.status === "contacted"}
                className="nx-btn-secondary"
              >
                Marcar contactado
              </button>
            )}
            {isVerified && (
              <button
                onClick={async () => {
                  const ok = await confirmAction({
                    title: d.status === "featured" ? "Quitar destaque" : "Marcar como destacado",
                    message: "Los destacados aparecen primero en el catálogo público.",
                    confirmLabel: d.status === "featured" ? "Quitar destaque" : "Destacar",
                  });
                  if (ok === true) updateStatus.mutate(d.status === "featured" ? "verified" : "featured");
                }}
                disabled={updateStatus.isPending}
                className="nx-btn-secondary"
              >
                <Star size={14} /> {d.status === "featured" ? "Quitar destaque" : "Destacar"}
              </button>
            )}
            {!isRejected && (
              <button
                onClick={async () => {
                  const ok = await confirmAction({
                    title: "Archivar proveedor",
                    message: "Se ocultará del catálogo pero queda en el sistema.",
                    confirmLabel: "Archivar",
                  });
                  if (ok === true) updateStatus.mutate("archived");
                }}
                disabled={updateStatus.isPending || d.status === "archived"}
                className="nx-btn-secondary"
              >
                <Archive size={14} /> Archivar
              </button>
            )}
            <button
              onClick={async () => {
                const reason = await confirmAction({
                  title: "Rechazar proveedor",
                  promptLabel: "Razón del rechazo (opcional)",
                  confirmLabel: "Rechazar",
                  tone: "danger",
                });
                if (reason !== null) reject.mutate((reason as string) || undefined);
              }}
              disabled={reject.isPending || isRejected}
              className="nx-btn-danger"
            >
              <XCircle size={14} /> Rechazar
            </button>
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Verificar proveedor",
                  message: `${d.businessName} quedará disponible para aparecer en el catálogo público.`,
                  confirmLabel: "Verificar",
                  tone: "success",
                });
                if (ok === true) verify.mutate();
              }}
              disabled={verify.isPending || isVerified}
              className="nx-btn-primary"
            >
              <CheckCircle2 size={14} /> Verificar
            </button>
          </>
        );
      }}
    />
  );
}
